import { useEffect, useRef } from 'react';
import { motion, useInView, useMotionValue, useTransform } from 'framer-motion';

import { Icon } from '@/components/icon';

interface TrustStat {
  value: number;
  suffix: string;
  label: string;
  sub: string;
  icon: 'Award' | 'TreeDeciduous' | 'Clock' | 'MapPin' | 'ShieldCheck';
}

const stats: TrustStat[] = [
  {
    value: 15,
    suffix: '+',
    label: 'Jahre am Baum',
    sub: 'Erfahrung & Routine',
    icon: 'Award',
  },
  {
    value: 200,
    suffix: '+',
    label: 'Projekte begleitet',
    sub: 'Pflege, Bau, Fällung',
    icon: 'TreeDeciduous',
  },
  {
    value: 24,
    suffix: '/7',
    label: 'Notfall-Bereitschaft',
    sub: 'Sturmschäden, Notfällung',
    icon: 'Clock',
  },
];

interface AnimatedCounterProps {
  to: number;
  suffix?: string;
}

function AnimatedCounter({ to, suffix = '' }: AnimatedCounterProps): React.ReactNode {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '-10%' });
  const motionValue = useMotionValue(0);
  const rounded = useTransform(motionValue, (latest) => Math.round(latest));

  useEffect(() => {
    if (!inView) {
      return;
    }
    const controls = { stop: () => undefined };
    let cancelled = false;
    const start = performance.now();
    const duration = 1400;

    const tick = (now: number) => {
      if (cancelled) {
        return;
      }
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      motionValue.set(eased * to);
      if (progress < 1) {
        requestAnimationFrame(tick);
      }
    };

    requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      controls.stop();
    };
  }, [inView, motionValue, to]);

  return (
    <span ref={ref} className="tabular-nums">
      <motion.span>{rounded}</motion.span>
      {suffix}
    </span>
  );
}

export function TrustBar(): React.ReactNode {
  return (
    <div className="relative border-y border-stone bg-paper">
      <div className="mx-auto max-w-6xl px-5 py-12 sm:px-8 sm:py-16">
        <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:gap-12">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-10%' }}
              transition={{ duration: 0.6, delay: index * 0.1, ease: [0.2, 0.7, 0.2, 1] }}
              className="border-l-2 border-moss/30 pl-5 last:col-span-2 sm:border-l-0 sm:border-t-2 sm:pl-0 sm:pt-5 sm:last:col-span-1"
            >
              <Icon name={stat.icon} className="mb-3 h-6 w-6 text-moss" />
              <p className="font-display text-[clamp(2.5rem,6vw,4.5rem)] font-semibold leading-none tracking-tight text-bark">
                <AnimatedCounter to={stat.value} suffix={stat.suffix} />
              </p>
              <p className="mt-3 text-base font-semibold tracking-tight text-bark sm:text-lg">
                {stat.label}
              </p>
              <p className="mt-1 text-sm text-mute">{stat.sub}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-stone pt-6 text-sm sm:mt-12 sm:pt-8"
        >
          <span className="inline-flex items-center gap-2 text-mute">
            <Icon name="ShieldCheck" className="h-4 w-4 text-moss" />
            Voll versichert · Betriebshaftpflicht
          </span>
          <span className="inline-flex items-center gap-2 text-mute">
            <Icon name="MapPin" className="h-4 w-4 text-moss" />
            Hamburg & Schleswig-Holstein
          </span>
          <span className="inline-flex items-center gap-2 text-mute">
            <Icon name="Mountain" className="h-4 w-4 text-moss" />
            Seilklettertechnik · ETW-zertifiziert
          </span>
        </motion.div>
      </div>
    </div>
  );
}
